import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  PhotoCamera as PhotoCameraIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import * as auth from '@/modules/auth/redux/AuthRedux';
import {
  getUserByToken,
  updateUserProfile,
} from '@/modules/auth/redux/AuthCRUD';
import { UserModel } from '@/modules/auth/models/UserModel';
import { changePassword } from '@/modules/users/redux/UserCRUD';
import { LabeledTextField } from '@/__core/components/LabeledField';
import AppForm, { AppFormSection } from '@/__core/components/AppForm';
import MessageSnackbar from '@/__core/helpers/components/MessageSnackbar';
import { formatErrorForSnackbar, toApiError } from '@/__core/helpers/Error';
import { useGlobalLoading } from '@/__core/hooks/useGlobalLoading';
import { logoutAndRedirectToLogin } from '@/modules/auth/utils/authUtils';
import {
  pickAvatarRawUrl,
  resolveProfileAvatarUrl,
} from '@/modules/auth/utils/avatarUtils';

type ProfileFormState = {
  fullName: string;
  email: string;
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

interface ProfileUpdateModalProps {
  open: boolean;
  onClose: () => void;
  user?: UserModel | null;
}

const emptyForm: ProfileFormState = {
  fullName: '',
  email: '',
  oldPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const LABEL_WIDTH = '150px';

const toDisplayText = (value: unknown): string => String(value ?? '').trim();

type ProfileApiUser = UserModel & {
  first_name?: string;
  last_name?: string;
  avatar?: string;
};

const resolveAvatarUrl = (user?: ProfileApiUser | null): string => {
  return resolveProfileAvatarUrl(user);
};

const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
  const trimmed = fullName.trim();
  const parts = trimmed.split(/\s+/).filter(Boolean);
  const firstName = parts[0] || '';
  const lastName = parts.slice(1).join(' ') || '';
  return { firstName, lastName };
};

const resolveInitials = (fullName: string, email: string): string => {
  const source = fullName.trim() || email;
  const parts = source.trim().split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  if (parts.length === 1 && parts[0].length > 0) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return 'U';
};

const normalizeAuthUser = (user: ProfileApiUser): UserModel => {
  const firstName = toDisplayText(user.firstname || user.first_name);
  const lastName = toDisplayText(user.lastname || user.last_name);
  const fullname = [firstName, lastName].filter(Boolean).join(' ');
  const rawAvatar = pickAvatarRawUrl(user);

  return {
    ...user,
    firstname: firstName,
    lastname: lastName,
    fullname: fullname || user.fullname,
    pic: rawAvatar,
    avatar: rawAvatar,
  } as UserModel & { avatar?: string };
};

const mapUserToForm = (user: ProfileApiUser): ProfileFormState => {

  const fullName =
    toDisplayText(user.fullname)
    || [toDisplayText(user.first_name || user.firstname), toDisplayText(user.last_name || user.lastname)]
      .filter(Boolean)
      .join(' ');

  return {
    fullName,
    email: toDisplayText(user.email),
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };
};

const validatePassword = (password: string): string | undefined => {
  if (!password.trim()) {
    return undefined;
  }
  if (password.length < 8) {
    return 'Mật khẩu phải có ít nhất 8 ký tự';
  }
  if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
    return 'Mật khẩu phải chứa ít nhất 1 chữ hoa, 1 chữ thường và 1 số';
  }
  return undefined;
};

export default function ProfileUpdateModal({ open, onClose, user }: ProfileUpdateModalProps) {
  const dispatch = useDispatch();
  const { startGlobalLoading, stopGlobalLoading } = useGlobalLoading();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<ProfileFormState>(emptyForm);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ProfileFormState, string>>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [avatarImageError, setAvatarImageError] = useState(false);

  const isDisabled = loadingProfile || saving;

  const avatarInitials = useMemo(
    () => resolveInitials(formData.fullName, formData.email),
    [formData.fullName, formData.email]
  );

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const resetAvatarSelection = () => {
    setAvatarFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const applyProfileUser = (profileUser: ProfileApiUser) => {
    setFormData(mapUserToForm(profileUser));
    setAvatarPreview(resolveAvatarUrl(profileUser));
    setAvatarImageError(false);
    resetAvatarSelection();
    setFieldErrors({});
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    let isActive = true;

    const loadProfile = async () => {
      setLoadingProfile(true);
      try {
        const response = await getUserByToken();
        if (!isActive) {
          return;
        }
        applyProfileUser(response.data as ProfileApiUser);
      } catch (error) {
        if (!isActive) {
          return;
        }
        if (user) {
          applyProfileUser(user as ProfileApiUser);
        } else {
          showSnackbar(formatErrorForSnackbar(toApiError(error)), 'error');
        }
      } finally {
        if (isActive) {
          setLoadingProfile(false);
        }
      }
    };

    void loadProfile();

    return () => {
      isActive = false;
    };
  }, [open, user]);

  const handleDialogClose = () => {
    if (saving) {
      return;
    }
    onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleAvatarPick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      showSnackbar('Vui lòng chọn file ảnh (JPG, PNG, ...)', 'error');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      showSnackbar('Ảnh đại diện tối đa 5MB', 'error');
      return;
    }

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
    setAvatarImageError(false);
  };

  const validateForm = (): boolean => {
    const nextErrors: Partial<Record<keyof ProfileFormState, string>> = {};

    const fullName = formData.fullName.trim();
    if (!fullName) {
      nextErrors.fullName = 'Họ tên là bắt buộc';
    } else if (fullName.length < 2) {
      nextErrors.fullName = 'Họ tên phải có ít nhất 2 ký tự';
    } else if (fullName.length > 100) {
      nextErrors.fullName = 'Họ tên không được quá 100 ký tự';
    }

    const wantsPasswordChange =
      formData.oldPassword.trim() ||
      formData.newPassword.trim() ||
      formData.confirmPassword.trim();

    if (wantsPasswordChange) {
      if (!formData.oldPassword.trim()) {
        nextErrors.oldPassword = 'Mật khẩu hiện tại là bắt buộc';
      }
      const newPasswordError = validatePassword(formData.newPassword);
      if (!formData.newPassword.trim()) {
        nextErrors.newPassword = 'Mật khẩu mới là bắt buộc';
      } else if (newPasswordError) {
        nextErrors.newPassword = newPasswordError;
      }
      if (!formData.confirmPassword.trim()) {
        nextErrors.confirmPassword = 'Xác nhận mật khẩu là bắt buộc';
      } else if (formData.confirmPassword !== formData.newPassword) {
        nextErrors.confirmPassword = 'Mật khẩu xác nhận không khớp';
      }
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      showSnackbar('Vui lòng kiểm tra lại thông tin đã nhập', 'error');
      return;
    }

    const userId = user?.id;
    if (!userId) {
      showSnackbar('Không xác định được tài khoản. Vui lòng đăng nhập lại.', 'error');
      return;
    }

    setSaving(true);
    startGlobalLoading('Đang cập nhật hồ sơ...', 'profile-update-modal');

    try {
      const { firstName, lastName } = splitFullName(formData.fullName);
      let updatedUser: UserModel;

      if (avatarFile) {
        const formPayload = new FormData();
        formPayload.append('first_name', firstName);
        formPayload.append('last_name', lastName);
        formPayload.append('avatar', avatarFile);
        formPayload.append('pic', avatarFile);
        const response = await updateUserProfile(formPayload);
        updatedUser = response.data;
      } else {
        const response = await updateUserProfile({
          first_name: firstName,
          last_name: lastName,
        });
        updatedUser = response.data;
      }

      const wantsPasswordChange =
        formData.oldPassword.trim() &&
        formData.newPassword.trim() &&
        formData.confirmPassword.trim();

      if (wantsPasswordChange) {
        await changePassword(String(userId), {
          old_password: formData.oldPassword,
          new_password: formData.newPassword,
          confirm_password: formData.confirmPassword,
        });

        showSnackbar(
          'Đã đổi mật khẩu thành công. Vui lòng đăng nhập lại.',
          'success'
        );
        onClose();

        window.setTimeout(() => {
          void logoutAndRedirectToLogin(dispatch);
        }, 1200);
        return;
      }

      const normalizedUser = normalizeAuthUser(updatedUser as ProfileApiUser);
      dispatch(auth.actions.setUser(normalizedUser));
      applyProfileUser(normalizedUser);
      showSnackbar('Đã cập nhật hồ sơ thành công.', 'success');
      onClose();
    } catch (error) {
      showSnackbar(formatErrorForSnackbar(toApiError(error)), 'error');
    } finally {
      setSaving(false);
      stopGlobalLoading('profile-update-modal');
    }
  };

  const sections = useMemo<AppFormSection[]>(() => {
    return [
      {
        key: 'profile-basic-info',
        title: '',
        fields: [
          {
            key: 'avatar',
            label: 'Ảnh đại diện',
            type: 'custom',
            standalone: true,
            gridSize: { xs: 12 },
            render: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', py: 1 }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar
                    src={!avatarImageError && avatarPreview ? avatarPreview : undefined}
                    imgProps={{
                      referrerPolicy: 'no-referrer',
                      onError: () => setAvatarImageError(true),
                    }}
                    sx={{
                      width: 88,
                      height: 88,
                      bgcolor: '#1e3a8a',
                      fontSize: 28,
                      fontWeight: 700,
                    }}
                  >
                    {avatarInitials}
                  </Avatar>
                  <IconButton
                    size='small'
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isDisabled}
                    sx={{
                      position: 'absolute',
                      right: -4,
                      bottom: -4,
                      bgcolor: 'white',
                      border: '1px solid #e2e8f0',
                      '&:hover': { bgcolor: '#f8fafc' },
                    }}
                  >
                    <PhotoCameraIcon fontSize='small' />
                  </IconButton>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='image/*'
                    hidden
                    onChange={handleAvatarPick}
                  />
                </Box>
                <Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
                  Nhấn biểu tượng máy ảnh để đổi ảnh đại diện
                </Typography>
              </Box>
            ),
          },
          {
            key: 'email',
            label: 'Email',
            labelWidth: '60px',
            type: 'text',
            disabled: true,
            value: formData.email || '',
            gridSize: { xs: 12, md: 6 },
            helperText: 'Email không thể thay đổi tại đây',
          },
          {
            key: 'fullName',
            label: 'Họ tên',
            labelWidth: '60px',
            type: 'text',
            required: true,
            disabled: isDisabled,
            value: formData.fullName || '',
            gridSize: { xs: 12, md: 6 },
            error: Boolean(fieldErrors.fullName),
            helperText: fieldErrors.fullName,
          },
        ],
      },
      {
        key: 'profile-password',
        title: 'Đổi mật khẩu (tùy chọn)',
        fields: [
          {
            key: 'oldPassword',
            label: 'Mật khẩu hiện tại',
            type: 'custom',
            standalone: true,
            disabled: isDisabled,
            gridSize: { xs: 12, md: 6 },
            render: () => (
              <LabeledTextField
                label='Mật khẩu hiện tại'
                labelWidth={LABEL_WIDTH}
                value={formData.oldPassword || ''}
                onChange={(e) => handleInputChange('oldPassword', e.target.value)}
                disabled={isDisabled}
                error={Boolean(fieldErrors.oldPassword)}
                helperText={fieldErrors.oldPassword}
                type={showOldPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        onClick={() => setShowOldPassword((v) => !v)}
                        edge='end'
                        tabIndex={-1}
                      >
                        {showOldPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            ),
          },
          {
            key: 'newPassword',
            label: 'Mật khẩu mới',
            type: 'custom',
            standalone: true,
            disabled: isDisabled,
            gridSize: { xs: 12, md: 6 },
            render: () => (
              <LabeledTextField
                label='Mật khẩu mới'
                labelWidth={LABEL_WIDTH}
                value={formData.newPassword || ''}
                onChange={(e) => handleInputChange('newPassword', e.target.value)}
                disabled={isDisabled}
                error={Boolean(fieldErrors.newPassword)}
                helperText={fieldErrors.newPassword}
                type={showNewPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        onClick={() => setShowNewPassword((v) => !v)}
                        edge='end'
                        tabIndex={-1}
                      >
                        {showNewPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            ),
          },
          {
            key: 'confirmPassword',
            label: 'Xác nhận mật khẩu mới',
            type: 'custom',
            standalone: true,
            disabled: isDisabled,
            gridSize: { xs: 12, md: 6 },
            render: () => (
              <LabeledTextField
                label='Xác nhận mật khẩu mới'
                labelWidth={LABEL_WIDTH}
                value={formData.confirmPassword || ''}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                disabled={isDisabled}
                error={Boolean(fieldErrors.confirmPassword)}
                helperText={fieldErrors.confirmPassword}
                type={showConfirmPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        size='small'
                        onClick={() => setShowConfirmPassword((v) => !v)}
                        edge='end'
                        tabIndex={-1}
                      >
                        {showConfirmPassword ? <VisibilityOff fontSize='small' /> : <Visibility fontSize='small' />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            ),
          },
        ],
      },
    ];
  }, [
    avatarInitials,
    avatarPreview,
    fieldErrors,
    formData,
    isDisabled,
    showConfirmPassword,
    showNewPassword,
    showOldPassword,
  ]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: {
            overflowX: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          <Typography variant='h6' component='span' sx={{ fontWeight: 600 }}>
            Cập nhật hồ sơ
          </Typography>
          <IconButton
            aria-label='Đóng'
            onClick={handleDialogClose}
            disabled={saving}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: 0,
            pb: 0,
            px: 0,
            overflowX: 'hidden',
            '&.MuiDialogContent-root': {
              paddingLeft: 0,
              paddingRight: 0,
              overflowX: 'hidden',
            },
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              overflowX: 'hidden',
              boxSizing: 'border-box',
              // Bù margin âm của Grid columnSpacing trong AppForm (spacing=5 → 20px mỗi bên)
              px: 2.5,
            }}
          >
            <AppForm
              sections={sections}
              values={formData as Record<string, string>}
              onChange={handleInputChange}
              onSubmit={() => void handleSave()}
              onCancel={handleDialogClose}
              submitting={saving}
              submitDisabled={isDisabled}
              hideActionBar
              containerSx={{
                m: 0,
                p: 0,
                py: 0,
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflowX: 'hidden',
                boxSizing: 'border-box',
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button onClick={handleDialogClose} disabled={saving}>
            Hủy
          </Button>
          <Button
            variant='contained'
            onClick={() => void handleSave()}
            disabled={isDisabled}
          >
            {saving ? 'Đang lưu...' : 'Cập nhật'}
          </Button>
        </DialogActions>
      </Dialog>

      <MessageSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
}
